import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BRAND } from '../brand';

// Número WhatsApp de la marca actual. SIN fallback: si no está seteado
// ni en REACT_APP_WHATSAPP_NUMBER ni en el preset, el botón NO se muestra.
// NUNCA caer al número de otra marca.
const PHONE_NUMBER =
  process.env.REACT_APP_WHATSAPP_NUMBER ||
  BRAND?.landing?.footerWhatsAppNumber ||
  BRAND?.whatsapp ||
  '';

// Mensajes prearmados según la página donde está el visitante.
// Si {BRAND.botName} recibe esto, puede arrancar directo en el paso correcto sin pedir intro.
const ROUTE_MESSAGES = {
  '/':              `Hola! Vengo desde la web de ${BRAND.name} y quiero cotizar un envio. Me ayudas?`,
  '/landing':       `Hola! Vengo desde la web de ${BRAND.name} y quiero cotizar un envio. Me ayudas?`,
  '/login':         `Hola! Tengo problemas para entrar a mi cuenta de ${BRAND.name}. Me podrias ayudar?`,
  '/register':      `Hola! Quiero registrarme en ${BRAND.name}. Necesito ayuda con el proceso.`,
  '/dashboard':     `Hola ${BRAND.botName}! Soy cliente de ${BRAND.name} y necesito ayuda.`,
  '/profile':       `Hola ${BRAND.botName}! Necesito ayuda con los datos de mi cuenta.`,
  '/transactions':  `Hola ${BRAND.botName}! Quiero consultar el estado de un envio que hice.`,
  '/send':          `Hola ${BRAND.botName}! Quiero hacer un envio nuevo. Me ayudas?`,
  '/credibol':      'Hola! Tengo dudas sobre CrediBol. Me explicas como funciona?',
  '/eliminar-cuenta': `Hola! Necesito ayuda para eliminar mi cuenta de ${BRAND.name}.`,
  '/privacidad':    `Hola! Tengo una duda sobre la politica de privacidad de ${BRAND.name}.`,
  '/terminos':      `Hola! Tengo una duda sobre los terminos de servicio de ${BRAND.name}.`,
};

const DEFAULT_MESSAGE = `Hola! Vengo desde la web de ${BRAND.name}. Me podrias ayudar?`;

const buildMessageForPath = (pathname) => {
  if (!pathname) return DEFAULT_MESSAGE;
  // Match exacto primero, luego match por prefijo (ej /transactions/123 → /transactions)
  if (ROUTE_MESSAGES[pathname]) return ROUTE_MESSAGES[pathname];
  const prefix = Object.keys(ROUTE_MESSAGES).find(
    (route) => route !== '/' && pathname.startsWith(route)
  );
  return prefix ? ROUTE_MESSAGES[prefix] : DEFAULT_MESSAGE;
};

const WhatsAppButton = ({ raised = false }) => {
  const location = useLocation();
  const message = useMemo(
    () => buildMessageForPath(location.pathname),
    [location.pathname]
  );

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);

  // Mostrar tooltip automatico despues de 4s, ocultarlo a los 12s
  // (en modo raised/dentro de la app NO se muestra: seria invasivo)
  useEffect(() => {
    if (tooltipDismissed || raised) return;
    const showTimer = setTimeout(() => setShowTooltip(true), 4000);
    const hideTimer = setTimeout(() => setShowTooltip(false), 12000);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [tooltipDismissed, raised]);

  const handleClick = () => {
    if (!PHONE_NUMBER) return; // guard: sin número configurado no abre nada
    const url = `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setShowTooltip(false);
    setTooltipDismissed(true);
  };

  // Regla Fase 8.a: sin número de marca configurado, el botón NO se muestra.
  // Nunca renderizar un fallback a otro número.
  if (!PHONE_NUMBER) return null;

  const dismissTooltip = (e) => {
    e.stopPropagation();
    setShowTooltip(false);
    setTooltipDismissed(true);
  };

  return (
    <div
      className={`fixed z-40 flex items-end gap-2 ${raised ? 'bottom-24 right-3' : 'bottom-6 right-6 z-50'}`}
      data-testid="whatsapp-floating"
    >
      {/* Tooltip / mensaje de invitacion (nunca dentro de la app) */}
      {showTooltip && !raised && (
        <div
          className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 px-4 py-3 max-w-[220px] mb-2 animate-fade-in-up"
          style={{ animation: 'fadeInUp 0.4s ease-out' }}
        >
          <button
            onClick={dismissTooltip}
            className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs font-bold transition"
            aria-label="Cerrar"
            data-testid="wa-tooltip-close"
          >
            ×
          </button>
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-gray-800 leading-tight mb-0.5">Hola! Soy {BRAND.botName}</p>
              <p className="text-xs text-gray-600 leading-tight">Disponible 24/7</p>
            </div>
          </div>
          {/* Cola del bocadillo */}
          <div className="absolute -bottom-2 right-6 w-3 h-3 bg-white border-r border-b border-gray-100 transform rotate-45"></div>
        </div>
      )}

      {/* Boton principal con anillo pulsante y badge online */}
      <button
        onClick={handleClick}
        onMouseEnter={() => !tooltipDismissed && setShowTooltip(true)}
        className="relative group"
        aria-label="Contactar por WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        {/* Pulso (solo fuera de la app, dentro es discreto) */}
        {!raised && (
          <span className="absolute inset-0 rounded-full bg-green-500 opacity-50 animate-ping pointer-events-none"></span>
        )}

        {/* Boton */}
        <span
          className={`relative flex items-center justify-center rounded-full shadow-2xl group-hover:scale-110 transition-transform duration-300 ${raised ? 'w-12 h-12' : 'w-16 h-16'}`}
          style={{ backgroundColor: '#25D366' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="white"
            className={raised ? 'w-7 h-7' : 'w-9 h-9'}
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </span>

        {/* Badge online */}
        <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 rounded-full bg-white shadow-md">
          <span className="block w-3 h-3 rounded-full bg-green-500 animate-pulse"></span>
        </span>
      </button>
    </div>
  );
};

export default WhatsAppButton;
